import { useEffect, useState } from 'react';

import { Outlet } from 'react-router-dom';

import LogRocket from 'logrocket';

import { MaintenancePage } from '@/pages/MaintenancePage';

import { useGlobalErrors } from '@/widgets/error';

import { useReadyNotificationsListener } from '@/features/notifications/readyNotifications';
import { AddEmailModal, useAddEmailModalStore } from '@/features/user/addUserEmail';

import { useEntrySource } from '@/entities/entrySource';
import { setupExtensionAuthListener } from '@/entities/recording';
import {
  useCurrentSpaceId,
  useGetSpaceCredits,
  useGetSpaces,
  useInitSpaces,
} from '@/entities/teamspace/model/useGetSpaces';
import { useCognitoCheckUser, useGetOrCreateUser, useSettings } from '@/entities/user/currentUser';

import { IS_LOGROCKET_DEV_MODE_ENABLED, IS_PRODUCTION_BUILD } from '@/shared/environments';
import { usePrevious } from '@/shared/hooks';
import { getExtensionId, redirectToExtension } from '@/shared/lib/extensionAuthSSO';
import { clearTusLSEntries } from '@/shared/lib/tus';
import { initChurnkey } from '@/shared/thirdParty/churnkey';
import { HubSpotChat, HubSpotUserUpdateEvents } from '@/shared/thirdParty/hubSpot';
import { useInitRick } from '@/shared/thirdParty/rick';
import { Loader } from '@/shared/ui/Loader';

import { IS_MAINTENANCE_DISABLED } from '../../config';
import { useBalance } from './hooks/useBalance';
import { useGTMAuthorization } from './hooks/useGTMAuthorization';

const LOGROCKET_KEY = import.meta.env.VITE_LOGROCKET;

const IS_LOGROCKET_ENABLED = !!LOGROCKET_KEY && (IS_PRODUCTION_BUILD || IS_LOGROCKET_DEV_MODE_ENABLED);

export const InitAppLayout = () => {
  useGlobalErrors();

  const [isInitialDataReceived, setIsInitialDataReceived] = useState(false);

  const openAddEmailModal = useAddEmailModalStore((state) => state.open);
  const { data: { isUsedSocialProviders } = {} } = useCognitoCheckUser();
  const { isAuthenticated, user } = useGetOrCreateUser({
    isSocial: !!isUsedSocialProviders,
    tiktokCallback: openAddEmailModal,
  });
  const {
    features: { maintenance },
  } = useSettings();
  const entrySource = useEntrySource();
  const isRecordingSource = entrySource.status === 'resolved' && entrySource.source === 'recording';

  const { isInitialized: isSpacesInitialized } = useInitSpaces(isAuthenticated && !isRecordingSource);
  const { data: spaces } = useGetSpaces(isAuthenticated && !isRecordingSource);
  const currentSpaceId = useCurrentSpaceId();

  const defaultSpaceId = spaces?.find((s) => s.is_default)?.id;

  useGetSpaceCredits(currentSpaceId);
  useBalance(currentSpaceId);
  useInitRick();
  useReadyNotificationsListener(isAuthenticated);

  const previousMaintenance = usePrevious(maintenance);

  useEffect(() => {
    const isAppDataInitialized = isRecordingSource || isSpacesInitialized;

    if (isAuthenticated && (!maintenance || IS_MAINTENANCE_DISABLED) && isAppDataInitialized) {
      setIsInitialDataReceived(true);
    }
  }, [isAuthenticated, isRecordingSource, maintenance, isSpacesInitialized, user]);

  useGTMAuthorization(user);

  useEffect(() => {
    if (!isRecordingSource || !user?.id) return;
    if (getExtensionId()) {
      void redirectToExtension(user.id);
    }
  }, [isRecordingSource, user?.id]);

  useEffect(() => {
    const isSwitchedFromMaintenance = previousMaintenance && !maintenance;

    if (isSwitchedFromMaintenance) {
      window.location.reload();
    }
  }, [maintenance, previousMaintenance]);

  useEffect(() => {
    if (user?.email) {
      if (IS_LOGROCKET_ENABLED) {
        LogRocket.init(LOGROCKET_KEY);
        LogRocket.identify(user.email);
      }

      HubSpotUserUpdateEvents.identifyUser(user.email);

      initChurnkey();
      clearTusLSEntries.expired();

      // Set up listener for Chrome extension silent auth requests
      setupExtensionAuthListener();
    }
  }, [user?.email]);

  if (maintenance && !IS_MAINTENANCE_DISABLED) {
    return <MaintenancePage />;
  }

  if (!isInitialDataReceived) {
    return (
      <>
        <Loader />
        <AddEmailModal />
      </>
    );
  }

  return (
    <>
      <Outlet />
      <HubSpotChat defaultSpaceId={defaultSpaceId} />
    </>
  );
};
